import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { UserForm } from './forms/UserForm';
import { useTeamStore } from '../../stores/teamStore';
import { createUserWithoutSignIn } from '../../lib/firebase/auth';
import { createUserProfile } from '../../lib/firebase/database';
import { User } from '../../types/user';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

export const AddUserModal = ({ isOpen, onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { roles, fetchUsers } = useTeamStore();
  const { translations } = useLanguage();

  const handleSubmit = async (data: Partial<User>) => {
    if (!data.email || !data.password || !data.name || !data.roleId) {
      setError(
        translations?.team?.addUser?.requiredFields || 'All fields are required'
      );
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Create Firebase Auth user without signing in
      const firebaseUser = await createUserWithoutSignIn(
        data.email,
        data.password
      );

      // Create user profile in Firestore
      const userData: User = {
        id: firebaseUser.uid,
        name: data.name,
        email: data.email,
        password: data.password, // Include password in the user data
        gender: data.gender || 'male',
        roleId: data.roleId,
        status: 'active',
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await createUserProfile(userData);
      await fetchUsers(); // Refresh users list
      toast.success(
        translations?.team?.addUser?.success || 'Member added successfully'
      );
      onClose();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(
        err.message ||
          translations?.team?.addUser?.error ||
          'An error occurred while adding the member'
      );
      toast.error(
        translations?.team?.addUser?.failure || 'Failed to add member'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.team?.addUser?.title || 'Add New Member'}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}
      <UserForm onSubmit={handleSubmit} roles={roles} isLoading={isLoading} />
    </Modal>
  );
};
